import * as React from 'react'
import moment from 'moment'

import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import ActivityItem from '@/modules/drivers/forms/activities/components/ActivityItem'
import { getActivityIcon } from '@/modules/drivers/helpers/ActivityIconResolver'
import { DateAndTimeField } from '@/ui/form/controls/DateAndTimeField'
import { FaIcon, faClose } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useVehicleActivityService from '@/modules/vehicles/services/ActivityService'
import { ActivityModel } from '@/app/models'

type Props = {
  companyId: string
  vehicleId: number
  activity: ActivityModel
  close: () => void
  onSuccess?: () => Promise<void> | void
}

const FIELD_FORMAT = 'YYYY-MM-DD HH:mm'
const REST = 0
const NO_DATA = 4

// Kolejność i zestaw jak w WPF ActivityTypeSelectorDialog.
const TYPES = [
  { value: 3, label: 'activityName.driving' },
  { value: 2, label: 'activityName.work' },
  { value: 1, label: 'activityName.availability' },
  { value: REST, label: 'activityName.rest' },
]

const ERROR_KEYS: Record<string, string> = {
  EDIT_VEHICLE_ACTIVITY_INVALID_DATES: 'vehicleActivityEdit.validation-dates',
  EDIT_VEHICLE_ACTIVITY_DATE_TOO_FAR: 'vehicleActivityEdit.validation-too-far',
  EDIT_VEHICLE_ACTIVITY_NOT_FOUND: 'vehicleActivityEdit.not-found',
}

const EditVehicleActivityModal: React.FC<Props> = ({
  companyId,
  vehicleId,
  activity,
  close,
  onSuccess,
}) => {
  const { t } = useLocalize()
  const { editVehicleActivity } = useVehicleActivityService()

  const [type, setType] = React.useState(
    activity.type === NO_DATA ? REST : activity.type,
  )
  const [dateFrom, setDateFrom] = React.useState(
    moment(activity.startTime).format(FIELD_FORMAT),
  )
  const [dateTo, setDateTo] = React.useState(
    moment(activity.endTime).format(FIELD_FORMAT),
  )
  const [isSaving, setIsSaving] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    const from = moment(dateFrom, FIELD_FORMAT, true)
    const to = moment(dateTo, FIELD_FORMAT, true)
    if (!from.isValid() || !to.isValid()) {
      setErrorMessage(t('vehicleActivityEdit.validation-dates'))
      return
    }
    if (from.isAfter(to)) {
      setErrorMessage(t('vehicleActivityEdit.validation-dates'))
      return
    }
    if (to.isAfter(moment().add(30, 'days'))) {
      setErrorMessage(t('vehicleActivityEdit.validation-too-far'))
      return
    }

    setIsSaving(true)
    try {
      await editVehicleActivity({
        companyId,
        vehicleId,
        activityId: activity.id,
        originalStartTime: moment(activity.startTime).toISOString(),
        type,
        startTime: from.toISOString(),
        endTime: to.toISOString(),
      })
      if (onSuccess) await onSuccess()
      close()
    } catch (error: any) {
      const code = error?.response?.data?.code
      setErrorMessage(t(ERROR_KEYS[code] ?? 'vehicleActivityEdit.error'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <ModalLayout>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="tw-relative tw-z-[10000] tw-flex tw-w-full tw-max-w-[450px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {isSaving && (
          <div className="tw-absolute tw-inset-0 tw-z-[10001] tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-black tw-bg-opacity-50">
            <div className="tw-h-12 tw-w-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-gray-200 tw-border-t-primary"></div>
          </div>
        )}

        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-6 tw-py-4">
          <p className="tw-text-lg tw-font-semibold tw-text-content-base">
            {t('vehicleActivityEdit.menu')}
          </p>
          <FaIcon
            icon={faClose}
            className="tw-h-5 tw-w-5 tw-cursor-pointer"
            onClick={close}
          />
        </div>

        <div className="tw-space-y-4 tw-p-6">
          <div className="tw-space-y-2">
            <p className="tw-text-sm tw-font-semibold tw-text-content-base">
              {t('vehicleActivityEdit.title')}
            </p>
            {TYPES.map((item) => (
              <ActivityItem
                key={item.value}
                checked={type === item.value}
                icon={getActivityIcon(item.value)}
                label={t(item.label)}
                onClick={() => {
                  setErrorMessage('')
                  setType(item.value)
                }}
              />
            ))}
          </div>

          <DateAndTimeField
            required
            label={t('vehicleActivityEdit.dateFrom')}
            value={dateFrom}
            setValue={(v) => {
              setErrorMessage('')
              setDateFrom(v ?? '')
            }}
          />

          <DateAndTimeField
            required
            label={t('vehicleActivityEdit.dateTo')}
            value={dateTo}
            setValue={(v) => {
              setErrorMessage('')
              setDateTo(v ?? '')
            }}
          />

          {errorMessage && (
            <p className="tw-mt-1 tw-text-sm tw-text-red-500">{errorMessage}</p>
          )}
        </div>

        <div className="tw-flex tw-justify-end tw-gap-3 tw-border-t tw-border-base-300 tw-px-6 tw-py-4">
          <button
            type="button"
            onClick={close}
            className="tw-rounded-lg tw-border tw-border-base-300 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base hover:tw-bg-base-200"
          >
            {t('vehicleActivityEdit.cancel')}
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-opacity-50"
          >
            {t('vehicleActivityEdit.change')}
          </button>
        </div>
      </form>
    </ModalLayout>
  )
}

export default EditVehicleActivityModal
